import React, { useState } from 'react';
import { useTenant } from '../contexts/TenantContext';
import { useAuthFetch } from '../hooks/useAuthFetch';
import { useUsage } from '../hooks/useUsage';

export const OrgPage: React.FC = () => {
  const { org, loading, role, canPerform, refresh } = useTenant();
  const authFetch = useAuthFetch();
  const { usage } = useUsage();
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'member' | 'admin'>('member');
  const [inviting, setInviting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [orgName, setOrgName] = useState('');

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviting(true);
    setMessage(null);
    try {
      const res = await authFetch('/api/orgs/me/members', {
        method: 'POST',
        body: JSON.stringify({ email: inviteEmail.trim(), role: inviteRole }),
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        if (data?.emailSent === false) {
          setMessage(`${data?.message || 'Invitation saved'} Email delivery is not configured yet.`);
        } else {
          setMessage(data?.message || `Invited ${inviteEmail} as ${inviteRole}`);
        }
        setInviteEmail('');
        refresh();
      } else {
        const err = await res.json().catch(() => ({ detail: 'Failed to invite' }));
        setMessage(err.detail || 'Failed to invite member');
      }
    } catch {
      setMessage('Failed to invite member');
    } finally {
      setInviting(false);
    }
  };

  const handleRemoveMember = async (uid: string) => {
    if (!window.confirm('Remove this member from the organization?')) return;
    try {
      const res = await authFetch(`/api/orgs/me/members/${uid}`, { method: 'DELETE' });
      if (res.ok) refresh();
    } catch { /* handled in UI */ }
  };

  const handleChangeRole = async (uid: string, newRole: string) => {
    try {
      const res = await authFetch(`/api/orgs/me/members/${uid}/role`, {
        method: 'PATCH',
        body: JSON.stringify({ role: newRole }),
      });
      if (res.ok) refresh();
    } catch { /* handled in UI */ }
  };

  const handleRename = async () => {
    if (!orgName.trim()) return;
    try {
      const res = await authFetch('/api/orgs/me', {
        method: 'PATCH',
        body: JSON.stringify({ name: orgName.trim() }),
      });
      if (res.ok) { setEditing(false); refresh(); }
    } catch { /* handled in UI */ }
  };

  if (loading) {
    return (
      <div className="page-container" style={{ padding: 40 }}>Loading organization...</div>
    );
  }

  if (!org) {
    return (
      <div className="page-container" style={{ padding: 40 }}>
        Organization information not available. Try refreshing.
      </div>
    );
  }

  return (
    <>
      <div className="page-container" style={{ maxWidth: 800, margin: '0 auto', padding: '24px 16px' }}>
        <h1 style={{ marginBottom: 8 }}>
          {editing ? (
            <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                value={orgName}
                onChange={e => setOrgName(e.target.value)}
                style={{ fontSize: 'inherit', padding: '4px 8px', borderRadius: 4, border: '1px solid var(--border-color)', background: 'var(--bg-surface)', color: 'var(--text-primary)' }}
                autoFocus
              />
              <button onClick={handleRename} style={{ fontSize: 14, padding: '4px 12px' }}>Save</button>
              <button onClick={() => setEditing(false)} style={{ fontSize: 14, padding: '4px 12px' }}>Cancel</button>
            </span>
          ) : (
            <span>
              {org.name}
              {canPerform('update_org') && (
                <button
                  onClick={() => { setOrgName(org.name); setEditing(true); }}
                  style={{ marginLeft: 12, fontSize: 14, background: 'none', border: 'none', color: '#4285f4', cursor: 'pointer' }}
                >
                  ✏️ Rename
                </button>
              )}
            </span>
          )}
        </h1>
        <p style={{ color: 'var(--text-muted)', marginBottom: 24 }}>
          Plan: <strong>{org.plan}</strong> · Your role: <strong>{role}</strong>
          {org.isPersonal && ' · Personal workspace'}
        </p>

        {/* Members */}
        <h2 style={{ marginBottom: 12 }}>Members ({org.members.length})</h2>
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 24 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border-color)', textAlign: 'left' }}>
              <th style={{ padding: '8px 4px' }}>Name</th>
              <th style={{ padding: '8px 4px' }}>Email</th>
              <th style={{ padding: '8px 4px' }}>Role</th>
              {canPerform('remove_member') && <th style={{ padding: '8px 4px' }}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {org.members.map(m => (
              <tr key={m.userId} style={{ borderBottom: '1px solid var(--border-color)' }}>
                <td style={{ padding: '8px 4px' }}>{m.displayName || '—'}</td>
                <td style={{ padding: '8px 4px' }}>{m.email}</td>
                <td style={{ padding: '8px 4px' }}>
                  {canPerform('change_role') && m.role !== 'owner' ? (
                    <select
                      value={m.role}
                      onChange={e => handleChangeRole(m.userId, e.target.value)}
                      style={{ background: 'var(--bg-surface)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', borderRadius: 4, padding: '2px 4px' }}
                    >
                      <option value="member">member</option>
                      <option value="admin">admin</option>
                    </select>
                  ) : (
                    m.role
                  )}
                </td>
                {canPerform('remove_member') && (
                  <td style={{ padding: '8px 4px' }}>
                    {m.role !== 'owner' && (
                      <button
                        onClick={() => handleRemoveMember(m.userId)}
                        style={{ color: '#f44', background: 'none', border: 'none', cursor: 'pointer' }}
                      >Remove</button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>

        {/* Invite */}
        {canPerform('invite_member') && (
          <>
            <h2 style={{ marginBottom: 12 }}>Invite Member</h2>
            {usage?.seats !== undefined && (
              <p style={{ color: 'var(--text-muted)', marginBottom: 8, fontSize: 14 }}>
                {(() => {
                  const limit = usage.usage?.seats?.limit ?? usage.seats;
                  const current = usage.usage?.seats?.current ?? org.members.length;
                  if (limit === -1) return `${current} seats used · Unlimited`;
                  return `${current} of ${limit} seats used (members + pending invites)`;
                })()}
                {(() => {
                  const limit = usage.usage?.seats?.limit ?? usage.seats;
                  const current = usage.usage?.seats?.current ?? org.members.length;
                  return limit !== -1 && current >= limit;
                })() && (
                  <span style={{ color: '#f44', marginLeft: 8 }}>· Seat limit reached — upgrade to invite more.</span>
                )}
              </p>
            )}
            <form onSubmit={handleInvite} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
              <input
                type="email"
                placeholder="Email address"
                value={inviteEmail}
                onChange={e => setInviteEmail(e.target.value)}
                required
                style={{ flex: 1, minWidth: 200, padding: '8px 12px', borderRadius: 4, border: '1px solid var(--border-color)', background: 'var(--bg-surface)', color: 'var(--text-primary)' }}
              />
              <select
                value={inviteRole}
                onChange={e => setInviteRole(e.target.value as 'member' | 'admin')}
                style={{ padding: '8px 12px', borderRadius: 4, border: '1px solid var(--border-color)', background: 'var(--bg-surface)', color: 'var(--text-primary)' }}
              >
                <option value="member">Member</option>
                <option value="admin">Admin</option>
              </select>
              <button
                type="submit"
                disabled={inviting}
                style={{ padding: '8px 16px', borderRadius: 4, background: '#4285f4', color: '#fff', border: 'none', cursor: 'pointer' }}
              >
                {inviting ? 'Inviting...' : 'Invite'}
              </button>
            </form>
            {message && <p style={{ color: message.startsWith('Invited') ? '#4caf50' : '#f44' }}>{message}</p>}
          </>
        )}
      </div>
    </>
  );
};
